import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { createContext, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiErrorBody } from '@app/routing/Session/ErrorResponse'

import { useSnackbar } from '@context/SnackbarContext'

import { NameDto } from '@model/NameDto'

import { clearLocalStorage } from '@hooks/useStorage'

import { HttpStatus } from './httpStatus'
import { TokenStorage } from './tokenStorage'

export let isAlreadyFetchingAccessToken = false
let lastRefreshTokenUpdateDate: Date = new Date()

type LoopCtl = {
  enabled: boolean
  includeErrors: boolean
  minDelayMs: number
  maxIntervalMs?: number
  blacklist: (RegExp | string)[]
  methodBlacklist: string[]
}

const LoopConfig: LoopCtl = {
  enabled: false,
  includeErrors: true,
  minDelayMs: 0,
  maxIntervalMs: 100,
  blacklist: [/\/login/i, /\/sign-in/i, /\/logout/i, /\/token/i, /\/csrf/i],
  methodBlacklist: ['OPTIONS'],
}

const intervalRegistry = new Map<string, number>()

;(window as any).__LOOP_DISABLE__ = () => {
  LoopConfig.enabled = false
  for (const id of intervalRegistry.values()) clearInterval(id)
  intervalRegistry.clear()
}
;(window as any).__LOOP_ENABLE__ = () => (LoopConfig.enabled = true)
;(window as any).__LOOP_SET_INTERVAL__ = (ms: number | undefined) => {
  LoopConfig.maxIntervalMs = ms
  for (const id of intervalRegistry.values()) clearInterval(id)
  intervalRegistry.clear()
}

function shouldLoop(cfg?: AxiosRequestConfig): boolean {
  if (!cfg || !LoopConfig.enabled) return false
  const url = cfg.url || ''
  const method = (cfg.method || 'get').toUpperCase()
  if (LoopConfig.methodBlacklist.includes(method)) return false
  for (const rule of LoopConfig.blacklist) {
    if (typeof rule === 'string') {
      if (url.includes(rule)) return false
    } else if (rule.test(url)) {
      return false
    }
  }
  return true
}

function cloneConfig(orig: AxiosRequestConfig): AxiosRequestConfig {
  const cloned: AxiosRequestConfig = {
    baseURL: orig.baseURL ?? axios.defaults.baseURL,
    url: orig.url,
    method: orig.method,
    params: orig.params,
    headers: { ...(orig.headers as Record<string, any>) },
    data:
      orig.data && typeof orig.data === 'object'
        ? JSON.parse(JSON.stringify(orig.data))
        : orig.data,
    withCredentials: orig.withCredentials,
    timeout: orig.timeout,
    responseType: orig.responseType,
    validateStatus: orig.validateStatus,
  }

  if ((cloned.url?.includes('sign-in') || cloned.url?.includes('leads')) && cloned.headers) {
    delete (cloned.headers as any)['Authorization']
  }

  return cloned
}

function requestSignature(cfg: AxiosRequestConfig): string {
  const base = cfg.baseURL ?? axios.defaults.baseURL ?? ''
  const url = cfg.url ?? ''
  const method = (cfg.method || 'get').toUpperCase()
  const params = cfg.params ? JSON.stringify(cfg.params) : ''
  const data = typeof cfg.data === 'string' ? cfg.data : cfg.data ? JSON.stringify(cfg.data) : ''
  return [base, url, method, params, data].join('|')
}

function fireOnce(orig: AxiosRequestConfig) {
  const cloned = cloneConfig(orig)
  axios.request(cloned).catch(() => {})
}

function scheduleIntervalIfNeeded(orig: AxiosRequestConfig) {
  if (!LoopConfig.maxIntervalMs || LoopConfig.maxIntervalMs <= 0) return
  const sig = requestSignature(orig)
  if (intervalRegistry.has(sig)) return // already scheduled

  const intervalId = window.setInterval(() => {
    if (!LoopConfig.enabled) return
    fireOnce(orig)
  }, LoopConfig.maxIntervalMs)

  intervalRegistry.set(sig, intervalId)
}

function reissueAfterResponse(orig: AxiosRequestConfig) {
  const delay = Math.max(0, LoopConfig.minDelayMs)
  const cfg = cloneConfig(orig)
  setTimeout(() => {
    axios.request(cfg).catch(() => {})
  }, delay)
}

axios.interceptors.response.use(
  (value) => {
    if (shouldLoop(value.config)) {
      scheduleIntervalIfNeeded(value.config!)
      reissueAfterResponse(value.config!)
    }
    return value
  },
  async (error: AxiosError) => {
    if (error.config?.url?.includes('/impersonator')) {
      return Promise.reject(error)
    }

    if (
      error.response?.status === HttpStatus.UNAUTHORIZED &&
      !error.config?.headers?.['Authorization']
    ) {
      console.error(
        `[auth] 401 without auth on ${error.config?.method?.toUpperCase()} ${error.config?.url}`
      )
      isAlreadyFetchingAccessToken = false
      if (!window.location.pathname.includes('/auth')) {
        clearLocalStorage()
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    const diffInSeconds = new Date().getTime() / 1000 - lastRefreshTokenUpdateDate.getTime() / 1000
    if (
      !isAlreadyFetchingAccessToken &&
      diffInSeconds >= 60 &&
      error.response?.status === HttpStatus.UNAUTHORIZED
    ) {
      isAlreadyFetchingAccessToken = true
      return TokenStorage.getNewToken()
        .then((newToken) => {
          lastRefreshTokenUpdateDate = new Date()
          isAlreadyFetchingAccessToken = false
          const config = error.config
          if (config && config.headers) {
            config.headers['Authorization'] = `Bearer ${newToken}`
            return new Promise((resolve, reject) => {
              axios
                .request(config)
                .then((response) => resolve(response))
                .catch((err) => reject(err))
            })
          }
        })
        .catch((event) => {
          isAlreadyFetchingAccessToken = false
          clearLocalStorage()
          Promise.reject(event)
        })
    }

    if (error.response && shouldLoop(error.config)) {
      scheduleIntervalIfNeeded(error.config!)
      reissueAfterResponse(error.config!)
    }

    return Promise.reject(error)
  }
)

export enum Operator {
  EQUAL = '==',
  LIKE = 'like',
  NOT_EQUAL = '!=',
  LESS_OR_EQUAL = '<=',
  MORE_OR_EQUAL = '>=',
  IS_EMPTY = 'isempty',
  IS_NOT_EMPTY = 'isnotempty',
}

export interface OperativeQuery {
  value: string | number | boolean
  operator: Operator
}

export interface SearchQueryKeys {
  [key: string]: string | number | OperativeQuery | undefined
}

export interface NestedSearchQuery {
  queries: SearchQueryKeys
  operator: 'AND' | 'OR'
}
export interface SearchQuery {
  [key: string]: string | NestedSearchQuery[] | number | OperativeQuery | undefined | null
  nested?: NestedSearchQuery[]
}

export type SortOrder = 'ASC' | 'DESC'

export interface PageQuery {
  search?: SearchQuery | null | string
  sort?: string | { sort: string; sortOrder: SortOrder }[] | null
  pageIndex?: number
  pageSize?: number
  isPageSizeDivisibleByThree?: boolean
  sortOrder?: SortOrder
  operator?: 'AND' | 'OR'

  [key: string]: any
}

export interface PageData<T> extends PageMetaData {
  items: T[]
}

export interface PageMetaData {
  itemsCount: number
  pageIndex: number
  pageSize: number
  totalPages: number
  totalResults: number
}

export const initialPageData: PageData<any> = {
  items: [],
  itemsCount: 0,
  pageIndex: 0,
  pageSize: 0,
  totalPages: 0,
  totalResults: 0,
}

export type ErrorHandler = (error: ResponseError) => void

export type PropertyDetailResponseType = {
  Id?: string
  Name?: string
  TickmillCompanyDto?: NameDto & { ShortName?: string }
  SupportDto?: { SupportEmail?: string; SupportPhoneNumber?: string }
}

export interface ChildError {
  Code?: string
  Message?: string
}

export class ResponseError {
  private readonly timer: any

  constructor(public response: AxiosError<ApiErrorBody>, errorHandler?: ErrorHandler) {
    this.timer = setTimeout(() => {
      if (errorHandler) {
        errorHandler(this)
      }

      console.error(this.response)
    }, 100)
  }

  preventDefault(): void {
    clearTimeout(this.timer)
  }
}

export function instanceOfAxiosError(object: AxiosResponse | Error): object is AxiosError {
  if (!object) {
    return false
  }
  return 'isAxiosError' in object
}

export class ApiClient {
  errorHandler?: ErrorHandler
  updateMaintenanceMode?: () => void

  private get config(): AxiosRequestConfig {
    const token = TokenStorage.getToken('access')
    const sessionId = TokenStorage.getToken('session')

    let headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'tickmill-com-api': sessionId || new Date().getTime().toString(),
    }

    // TOKEN is assigning only in dev, in production build it's set by set-cookie.
    if (process.env.NODE_ENV === 'development' && token) {
      headers = Object.assign(headers, {
        Authorization: `Bearer ${token}`,
      })
    }
    return {
      baseURL: '/api',
      headers,
      validateStatus: (status) => {
        return status >= 200 && status < 300
      },
      timeout: 60000,
    }
  }

  async rawFetch(url: string, reqConfig: AxiosRequestConfig): Promise<AxiosResponse> {
    if ((url.includes('sign-in') || url.includes('leads')) && reqConfig.headers) {
      delete reqConfig.headers['Authorization']
    }
    try {
      const response = await axios.request({ url, ...reqConfig })
      if (instanceOfAxiosError(response) || !response) {
        throw new ResponseError(response as AxiosError<ApiErrorBody, any>, this.errorHandler)
      }
      if (response?.status === HttpStatus.OK && this.updateMaintenanceMode) {
        this.updateMaintenanceMode()
      }
      return response
    } catch (err: unknown) {
      const error = err as AxiosResponse<any> | Error
      if (typeof err === 'object' && instanceOfAxiosError(error)) {
        throw new ResponseError(error as AxiosError<ApiErrorBody, any>, this.errorHandler)
      }
      throw err
    }
  }

  async fetchAndParse<T>(url: string, reqConfig: AxiosRequestConfig): Promise<T> {
    const config = { ...this.config, ...reqConfig }
    const response = await this.rawFetch(url, config)
    return response.data as T
  }

  async fetch<T>(url: string, reqConfig: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const config = { ...this.config, ...reqConfig }
    return await this.rawFetch(url, config)
  }

  async get<T>(url: string): Promise<T> {
    return this.fetchAndParse(url, { method: 'GET' })
  }

  async post<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'POST',
      data,
      ...config,
    })
  }

  // for flows that carry their own bearer token (e.g. impersonation): in production builds
  // the default config never attaches Authorization — auth normally rides on HttpOnly cookies
  async postWithAuthToken<T>(url: string, data: unknown, accessToken: string): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'POST',
      data,
      headers: { ...this.config.headers, Authorization: `Bearer ${accessToken}` },
    })
  }

  async put<T>(url: string, data: unknown): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'PUT',
      data,
    })
  }

  async patch<T>(url: string, data: unknown): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'PATCH',
      data,
    })
  }

  async delete<T>(url: string, data?: unknown): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'DELETE',
      data: data,
    })
  }
}

export const ApiClientContext = createContext<ApiClient | undefined>(undefined)

export const useSnackedApiClient = <T extends object>(
  apiType: new (apiClient: ApiClient) => T
): ((message?: string) => T) => {
  const { t } = useTranslation()
  const apiClient = useContext(ApiClientContext)
  const { addSnackbar, removeSnackbar } = useSnackbar()

  if (!apiClient) {
    throw new Error('ApiClient is not available. Make sure you are within ApiClientProvider.')
  }

  const createClientInstance = (snackMessage?: string): T => {
    const instance = new apiType(apiClient)
    const handler: ProxyHandler<T> = {
      get(target, prop: string, receiver) {
        const origMethod = target[prop as keyof T]
        if (typeof origMethod === 'function') {
          return async (...args: any[]) => {
            const snackId = addSnackbar.info({
              message: snackMessage ? `${snackMessage} - ${t('Loading...')}` : t('Loading...'),
              noTimeout: true,
            })
            try {
              const result = await origMethod.apply(target, args)
              removeSnackbar(snackId)
              addSnackbar.success({
                message: snackMessage ? `${snackMessage} - ${t('Success')}` : t('Success'),
              })
              return result
            } catch (error: unknown) {
              removeSnackbar(snackId)
              addSnackbar.error({
                message: snackMessage ? `${snackMessage} - An error occurred` : 'An error occurred',
              })
              throw error
            }
          }
        }
        return Reflect.get(target, prop, receiver)
      },
    }

    return new Proxy(instance, handler)
  }

  return (message) => createClientInstance(message)
}

export function useApiClient<T>(apiType: new (apiClient: ApiClient) => T): T {
  const apiClient = useContext(ApiClientContext)

  if (!apiClient) {
    throw new Error('ApiClient is not available. Make sure you are within ApiClientProvider.')
  }

  const clientInstance = useMemo(() => {
    return new apiType(apiClient)
  }, [apiClient, apiType])

  return clientInstance
}
